<template>
  <div class="phone-verification">
    <!-- Phone input with verification buttons -->
    <div class="d-flex">
      <input
        class="field__input flex-fill google-sans-code"
        v-model="inputPhoneNumber"
        @input="$emit('input', inputPhoneNumber)"
        type="tel"
        :class="{ invalid: validationError || shouldDisplayNotVerifiedMessage }"
        :placeholder="placeholder"
        :disabled="false === changingPhoneNumberIsAvailable"
      />
      <SharedButton
        v-if="currentPhoneNumberIsVerified"
        :disabled="true"
        class="phone-verification__send-sms ml-3 w-auto text-nowrap s-btn-bg-primary"
      >
        {{ trans('mobile_verified', '已驗證') }}
      </SharedButton>
      <SharedButton
        v-else
        :disabled="false === sendingCodeIsAvailable"
        class="phone-verification__send-sms ml-3 w-auto text-nowrap s-btn-bg-primary"
        @click="sendMobile"
      >
        <template v-if="sendingCodeIsAvailable">{{ trans('send_sms_code', '傳送驗證碼') }}</template>
        <template v-else-if="verificationCodeIsThrottled">{{ counterMessage }}</template>
        <template v-else-if="isEditingPhoneNumber && isSendingVerificationCode">傳送中...</template>
      </SharedButton>
      <button
        v-if="showEditButton"
        @click="enableEdit"
        class="btn btn-link text-nowrap ml-3"
      >
        {{ trans('revise', '修改') }}
      </button>
      <button
        v-if="showCancelButton"
        @click="cancel"
        class="btn btn-link text-nowrap ml-3"
      >
        {{ trans('cancel', '取消') }}
      </button>
    </div>

    <!-- Field memo -->
    <small v-if="fieldMemo" class="field__desc">{{ fieldMemo }}</small>

    <!-- Error messages -->
    <div v-if="mobileValidationMessage" class="phone-verification__error-message">{{ mobileValidationMessage }}</div>

    <!-- Verification required error -->
    <div v-if="shouldDisplayNotVerifiedMessage" class="phone-verification__error-message">
      {{ trans('not_verify_phone_number', '手機號碼未驗証') }}
    </div>

    <!-- Code input section -->
    <div v-if="isWaitingForCodeSubmission" class="phone-verification__code-submission my-3">
      <div class="flex-fill">
        <CodeInput
          :loading="false"
          :fields="6"
          fieldWidth="100%"
          :fieldHeight="50"
          :class="{ invalid: !codeIsValid }"
          :disabled="isSubmittingSms"
          @change="handleCodeChange"
        />
        <div v-if="!codeIsValid" class="phone-verification__error-message">{{ codeValidationMessage }}</div>
      </div>
      <div class="flex-fill">
        <SharedButton
          class="phone-verification__submit-code text-nowrap s-btn-bg-primary"
          :disabled="verificationCode.length != 6 || isSubmittingSms"
          @click="verifyMobile"
        >
          {{ isSubmittingSms ? '驗證中...' : trans('confirm', '確定') }}
        </SharedButton>
      </div>
    </div>
  </div>
</template>

<script>
import { uniq } from "lodash";
import { mapActions, mapGetters, mapState } from "vuex";
import registerApi from "@/apis/liff/v2/register";
import CodeInput from "@/components/CodeInput";
import SharedButton from "@/components/Page/Liff/Shared/Button";

const TIMEOUT = 120;

export default {
  name: "PhoneVerification",
  components: {
    CodeInput,
    SharedButton,
  },
  props: {
    value: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "請輸入您的手機號碼",
    },
    validationError: {
      type: Boolean,
      default: false,
    },
    fieldMemo: {
      type: String,
      default: "",
    },
    shouldLoadCustomerData: {
      type: Boolean,
      default: false,
    },
    shouldSyncCustomerData: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      inputPhoneNumber: null,
      verifiedPhoneNumbers: [],
      smsThrottleExpirations: {},
      verificationCode: "",
      currentTimestamp: Date.now(),
      timer: null,
      codeIsValid: true,
      isSubmittingSms: false,
      hasCalledValidate: false,
      isEditingPhoneNumber: true,
      mobileValidationMessage: null,
      codeValidationMessage: null,
      hasSentVerificationCode: false,
      isSendingVerificationCode: false,
    };
  },
  computed: {
    ...mapGetters("liffAuth", ["user"]),
    ...mapState("liffPhoneVerification", ["lastVerifiedPhone"]),
    userPhoneWasVerified() {
      return null !== this.user.phone_verified_at;
    },
    currentPhoneNumberIsVerified() {
      return (this.userPhoneWasVerified && this.inputPhoneNumber === this.user.mobile_phone)
        || this.verifiedPhoneNumbers.includes(this.inputPhoneNumber);
    },
    shouldDisplayNotVerifiedMessage() {
      return this.hasCalledValidate && !this.currentPhoneNumberIsVerified;
    },
    counterMessage() {
      if (!this.currentPhoneRecord) return '';
      const remainingSeconds = Math.ceil((this.currentPhoneRecord.expiresAt - this.currentTimestamp) / 1000);
      return this.trans("sms_code_expire_in", "{second} 秒後失效", {
        second: Math.max(0, remainingSeconds),
      });
    },
    lastVerifiedPhoneInSession() {
      return this.verifiedPhoneNumbers[this.verifiedPhoneNumbers.length - 1];
    },
    showEditButton() {
      return this.lastVerifiedPhoneInSession && !this.isEditingPhoneNumber;
    },
    showCancelButton() {
      return this.lastVerifiedPhoneInSession && this.isEditingPhoneNumber;
    },
    currentPhoneRecord() {
      return this.smsThrottleExpirations[this.inputPhoneNumber];
    },
    verificationCodeIsThrottled() {
      return this.currentPhoneRecord && this.currentTimestamp < this.currentPhoneRecord.expiresAt;
    },
    changingPhoneNumberIsAvailable() {
      return [
        this.isEditingPhoneNumber,
        !this.isSendingVerificationCode,
        !this.isSubmittingSms,
        !this.isWaitingForCodeSubmission,
      ].every(Boolean);
    },
    sendingCodeIsAvailable() {
      return [
        this.isEditingPhoneNumber,
        !this.verifiedPhoneNumbers.includes(this.inputPhoneNumber),
        !this.isSendingVerificationCode,
        !this.verificationCodeIsThrottled,
      ].every(Boolean);
    },
    isWaitingForCodeSubmission() {
      return [
        this.isEditingPhoneNumber,
        this.hasSentVerificationCode,
        this.currentPhoneRecord,
        !this.verifiedPhoneNumbers.includes(this.inputPhoneNumber),
      ].every(Boolean);
    },
  },
  mounted() {
    this.inputPhoneNumber = this.value;

    if (this.shouldLoadCustomerData) {
      this.inputPhoneNumber = this.user.mobile_phone || this.value;
      this.$emit("input", this.inputPhoneNumber);

      if (this.userPhoneWasVerified) {
        this.verifiedPhoneNumbers.push(this.user.mobile_phone);
      }
    }

    if (this.lastVerifiedPhone) {
      this.verifiedPhoneNumbers.push(this.lastVerifiedPhone);
      if (!this.inputPhoneNumber) {
        this.inputPhoneNumber = this.lastVerifiedPhone;
        this.$emit("input", this.inputPhoneNumber);
      }
    }

    this.isEditingPhoneNumber = !this.currentPhoneNumberIsVerified;
  },
  methods: {
    ...mapActions("liffAuth", ["fetchMe"]),
    ...mapActions("liffPhoneVerification", ["setLastVerifiedPhone"]),
    validate() { // Public API
      this.hasCalledValidate = true;
      return this.currentPhoneNumberIsVerified;
    },
    handleCodeChange(code) {
      this.verificationCode = code;
    },
    async sendMobile() {
      this.isSendingVerificationCode = true;
      this.startTimer();

      try {
        const response = await registerApi.sendPhone(this.inputPhoneNumber)
        if (response.data.status == "success") {
          this.hasSentVerificationCode = true;
          const now = Date.now();
          this.currentTimestamp = now;
          this.$set(this.smsThrottleExpirations, this.inputPhoneNumber, {
            expiresAt: now + (TIMEOUT * 1000)
          });
          this.mobileValidationMessage = null;
        }
      } catch (error) {
        console.log("send sms code error", error);
        this.mobileValidationMessage = this.trans("sms_send_error", "簡訊驗證發送出現問題");
      }

      this.isSendingVerificationCode = false;
    },
    async verifyMobile() {
      this.isSubmittingSms = true;
      this.codeValidationMessage = null;

      try {
        const response = await registerApi.verifyByMobile(
          this.verificationCode,
          this.inputPhoneNumber,
          this.shouldSyncCustomerData
        )

        if (response.data.status == "success") {
          if (this.shouldLoadCustomerData) {
            this.fetchMe();
          }

          this.codeIsValid = true;
          this.isEditingPhoneNumber = false;
          this.hasSentVerificationCode = false;
          this.setLastVerifiedPhone(this.inputPhoneNumber);
          this.$set(this, 'verifiedPhoneNumbers', uniq([...this.verifiedPhoneNumbers, this.inputPhoneNumber]));
        }
      } catch (error) {
        this.codeIsValid = false;
        this.codeValidationMessage = error.response.data.message;
      }

      this.isSubmittingSms = false;
    },
    startTimer() {
      this.clearTimer();
      this.timer = window.setInterval(() => {
        this.currentTimestamp = Date.now();
      }, 1000);
    },
    clearTimer() {
      if (!this.timer) {
        return;
      }

      window.clearInterval(this.timer);
      this.timer = null;
    },
    enableEdit() {
      this.isEditingPhoneNumber = true;
    },
    cancel() {
      this.isEditingPhoneNumber = false;
      this.inputPhoneNumber = this.lastVerifiedPhoneInSession || this.inputPhoneNumber;
      this.$emit("input", this.inputPhoneNumber);
    },
    getModuleConfig(key) {
      return this.$store.getters["liffModule/getConfig"]('member_register', key);
    },
    trans(key, defaultMessage, params) {
      let trans = this.getModuleConfig(`trans.${key}`) || defaultMessage;

      if (params) {
        trans = trans.replace(/\{(\w+)\}/g, (_, key) => params[key] || '');
      }

      return trans
    },
  },
  beforeDestroy() {
    this.clearTimer();
  },
};
</script>

<style lang="scss" scoped>
@use "@/assets/scss/common/fonts";
@use "@/assets/scss/shared/components/_fields";

.phone-verification {
  width: 100%;

  &__error-message {
    color: #fe0000;
    font-size: 12px;
    margin-top: 8px;
  }

  &__code-submission {
    display: flex;
    gap: 16px;
  }
}
</style>
